"use client";

import { useState, useEffect } from "react";
import { PiForkKnifeFill } from "react-icons/pi";
import { FaPlus, FaTimes, FaClock, FaWineGlass } from "react-icons/fa";

export default function ManageMenuPanel({
  restaurant,
  onClose,
  dishAnalyticsData,
  wineAnalyticsData,
  userRole,
}) {
  const [activeTab, setActiveTab] = useState("food");
  const [showAllUpdatesModal, setShowAllUpdatesModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Food analytics data mapping
  const foodAnalytics = {
    totalDishes: dishAnalyticsData?.totalDishes || 0,
    newItems: dishAnalyticsData?.newDishesInThirtyDays || 0,
    removedItems: dishAnalyticsData?.removedDishesInThirtyDays || 0,
    lastUpdated: dishAnalyticsData?.lastUpdatedDish || "2 hours ago",
    categoryDistribution: dishAnalyticsData?.dishTypesWithPercentages?.map(
      (cat) => ({
        name: cat.type === "Main" ? "Main Course" : `${cat.type}`,
        percentage: Number(cat.percentage || 0).toFixed(1),
        count: cat.count || 0,
      })
    ) || [],
  };

  // Wine analytics data mapping
  const wineAnalytics = {
    totalWines: wineAnalyticsData?.totalWines || 0,
    newWines: wineAnalyticsData?.newWinesInThirtyDays || 0,
    removedWines: wineAnalyticsData?.removedWinesInThirtyDays || 0,
    lastUpdated: wineAnalyticsData?.lastUpdatedWine || "—",
    recentWineUpdates: wineAnalyticsData?.recentWineUpdates || [],
    categoryDistribution: [
      { name: "Sparkling Wine", count: wineAnalyticsData?.sparklingWineCount || 0, percentage: Number(wineAnalyticsData?.sparklingWinePercentage || 0).toFixed(1), color: "#FACC15" },
      { name: "White Wine", count: wineAnalyticsData?.whiteWineCount || 0, percentage: Number(wineAnalyticsData?.whiteWinePercentage || 0).toFixed(1), color: "#FEF3C7" },
      { name: "Rosé Wine", count: wineAnalyticsData?.roseWineCount || 0, percentage: Number(wineAnalyticsData?.roseWinePercentage || 0).toFixed(1), color: "#FB7185" },
      { name: "Red Wine", count: wineAnalyticsData?.redWineCount || 0, percentage: Number(wineAnalyticsData?.redWinePercentage || 0).toFixed(1), color: "#881337" },
      { name: "Orange Wine", count: wineAnalyticsData?.orangeWineCount || 0, percentage: Number(wineAnalyticsData?.orangeWinePercentage || 0).toFixed(1), color: "#FB923C" },
      { name: "Dessert Wine", count: wineAnalyticsData?.dessertWineCount || 0, percentage: Number(wineAnalyticsData?.dessertWinePercentage || 0).toFixed(1), color: "#D97706" },
    ],
    subCategoryDistributionOfRedWine: wineAnalyticsData?.subCategoryDistributionOfRedWine || [
      { name: "Mild, Mannered Red", count: wineAnalyticsData?.subCategoryCountOfRedWineMildMannered || 0, percentage: Number(wineAnalyticsData?.subCategoryCountOfRedWineMildManneredPercentage || 0).toFixed(1) },
      { name: "Soft, Fruity Red", count: wineAnalyticsData?.subCategoryCountOfRedWineSoftFruity || 0, percentage: Number(wineAnalyticsData?.subCategoryCountOfRedWineSoftFruityPercentage || 0).toFixed(1) },
      { name: "Fresh, Spicy Red", count: wineAnalyticsData?.subCategoryCountOfRedWineFreshSpicy || 0, percentage: Number(wineAnalyticsData?.subCategoryCountOfRedWineFreshSpicyPercentage || 0).toFixed(1) },
      { name: "Powerful Red", count: wineAnalyticsData?.subCategoryCountOfRedWinePowerful || 0, percentage: Number(wineAnalyticsData?.subCategoryCountOfRedWinePowerfulPercentage || 0).toFixed(1) },
    ],
    regions: wineAnalyticsData?.regions || {},
    varietals: {
      cabernetSauvignon: wineAnalyticsData?.cabernetSauvignon || 0,
      chardonnay: wineAnalyticsData?.chardonnay || 0,
      pinotNoir: wineAnalyticsData?.pinotNoir || 0,
      sauvignonBlanc: wineAnalyticsData?.sauvignonBlanc || 0,
    },
    characteristics: {
      organic: wineAnalyticsData?.characteristics?.['Organic'] || 0,
      biodynamic: wineAnalyticsData?.characteristics?.['Biodynamic'] || 0,
      vegan: wineAnalyticsData?.characteristics?.['Vegan'] || 0,
      unfiltered: wineAnalyticsData?.characteristics?.['Unfiltered'] || 0,
      dryWines: wineAnalyticsData?.characteristics?.['Dry Wines'] || 0,
      residualSugar: wineAnalyticsData?.characteristics?.['Residual Sugar'] || 0,
    },
    styleDistribution: wineAnalyticsData?.styleDistribution || {},
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const currentData = activeTab === "food" ? foodAnalytics : wineAnalytics;

  const timeSince = (iso) => {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const mapActionToTitle = (action) => {
    if (!action) return "Item Update";
    if (action.includes("created")) return "Item Created";
    if (action.includes("updated")) return "Item Updated";
    if (action.includes("archived")) return "Item Archived";
    if (action.includes("restored")) return "Item Restored";
    return action;
  };
  const formatDate = (date) => {
    if (!date) return "—";

    const now = new Date();
    const updatedDate = new Date(date);
    const diffMs = now - updatedDate;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // 24 hours ke andar
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    }

    // 30 days (1 month) ke andar
    if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    }

    // Otherwise actual date
    return updatedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[500px] lg:w-[500px] bg-[#FFFAF2] border-l border-gray-200 shadow-2xl z-50 overflow-y-auto ">
      {/* Header Section */}
      <div className="p-6 pb-4 bg-white border-b border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-lg font-bold text-[#1F2937] flex items-center gap-2 leading-[1.2] max-w-[280px]">
              Manage Menu <span className="text-gray-300">—</span> {restaurant?.name}
            </h1>
            <p className="text-gray-400 font-medium text-[14px] mt-0.5">
              Comprehensive menu analytics and management tools
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <div className="p-5">
        {/* Navigation Tabs */}
        <div className="flex items-center gap-1 bg-white p-1 rounded border border-gray-100 shrink-0 shadow-sm mb-5 w-fit">
          <button
            onClick={() => setActiveTab("food")}
            className={`px-6 py-2 rounded font-bold text-sm transition-all duration-300 ${activeTab === "food"
              ? "bg-[#881337] text-white shadow-sm"
              : "text-gray-400 hover:text-gray-600"
              }`}
          >
            Food Menu
          </button>
          <button
            onClick={() => setActiveTab("wine")}
            className={`px-6 py-2 rounded font-bold text-sm transition-all duration-300 ${activeTab === "wine"
              ? "bg-[#881337] text-white shadow-sm"
              : "text-gray-400 hover:text-gray-600"
              }`}
          >
            Wine List
          </button>
        </div>

        {/* Analytics Statistics Row */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {/* Total Items Card */}
          <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)] flex items-center justify-between transition-all hover:shadow-md">
            <div>
              <p className="text-[14px] text-slate-400 font-medium mb-1">
                Total {activeTab === "food" ? "Dishes" : "Wines"}
              </p>
              <h3 className="text-2xl font-bold text-[#1F2937] leading-none">
                {activeTab === "food" ? foodAnalytics.totalDishes : wineAnalytics.totalWines}
              </h3>
            </div>
            <div className="flex items-center justify-center">
              {activeTab === "food" ? (
                <div className="w-9 h-9 bg-[#FFF1F2] rounded-full text-[#881337] flex items-center justify-center">
                  <PiForkKnifeFill size={16} />
                </div>
              ) : (
                <div className="text-3xl filter drop-shadow-sm">🍷</div>
              )}
            </div>
          </div>

          {/* New Items Card */}
          <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)] flex items-center justify-between transition-all hover:shadow-md">
            <div>
              <p className="text-[14px] text-slate-400 font-medium mb-1">
                New {activeTab === "food" ? "Items" : "Wines"} (30 days)
              </p>
              <h3 className={`text-2xl font-bold leading-none ${activeTab === "wine" ? "text-[#059669]" : "text-[#1F2937]"}`}>
                {activeTab === "food" ? foodAnalytics.newItems : wineAnalytics.newWines}
              </h3>
            </div>
            <div className="flex items-center justify-center">
              {activeTab === "food" ? (
                <div className="w-8 h-8 bg-[#FEF3C7]/80 rounded-full flex items-center justify-center">
                  <div className="w-5 h-5 bg-[#FCD34D] rounded-full text-white flex items-center justify-center">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 2.5V9.5M2.5 6H9.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 px-1 py-0.5 rounded shadow-sm">
                  <span className="text-[9px] font-black text-white italic">NEW</span>
                </div>
              )}
            </div>
          </div>

          {/* Removed Items Card */}
          <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)] flex items-center justify-between transition-all hover:shadow-md">
            <div>
              <p className="text-[14px] text-slate-400 font-medium mb-1">
                Removed Items (30 days)
              </p>
              <h3 className={`text-2xl font-bold leading-none ${activeTab === "wine" ? "text-[#DC2626]" : "text-[#1F2937]"}`}>
                {activeTab === "food" ? currentData.removedItems : currentData.removedWines}
              </h3>
            </div>
            <div className="flex items-center justify-center">
              {activeTab === "food" ? (
                <div className="w-8 h-8 bg-[#FFF1F2]/80 rounded-full flex items-center justify-center">
                  <div className="w-5 h-5 bg-[#E11D48] rounded-full text-white flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 2.5L7.5 7.5M7.5 2.5L2.5 7.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
                    </svg>
                  </div>
                </div>
              ) : (
                <FaTimes size={28} className="text-[#DC2626]" />
              )}
            </div>
          </div>

          {/* Last Updated Card */}
          <div className="bg-white p-3.5 rounded-xl border border-gray-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)] flex items-center justify-between transition-all hover:shadow-md">
            <div className="min-w-0 pr-2">
              <p className="text-[14px] text-slate-400 font-medium mb-1">
                Last Updated
              </p>
              <h3 className="text-base font-bold text-[#1F2937] leading-none mb-0.5">
                {activeTab === "wine" ? formatDate(restaurant.updatedAt) : formatDate(restaurant.updatedAt)}
              </h3>
            </div>
            <div className="flex items-center justify-center">
              {activeTab === "food" ? (
                <div className="w-9 h-9 bg-[#FEF3C7] rounded-full text-[#92400E] flex items-center justify-center flex-shrink-0">
                  <FaClock size={15} />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-100/10 flex items-center justify-center shadow-[inset_0_1px_4px_rgba(0,0,0,0.1)] border border-slate-200">
                  <FaClock size={18} className="text-slate-400" />
                </div>
              )}
            </div>
          </div>
        </div>



        {/* Category Distribution Chart Card */}
        {activeTab === "food" ? (
          <div className="bg-white rounded-2xl p-2 border border-[#FDE68A] shadow-[0_1px_10px_-2px_rgba(0,0,0,0.05)] max-w-full lg:max-w-[100%]">
            <h3 className="text-[14px] font-bold text-[#92400E] uppercase tracking-wide text-center mb-6">
              Menu Category Distribution
            </h3>
            <div className="space-y-3">
              {foodAnalytics.categoryDistribution.map((category, index) => {
                const colors = [
                  "#E11D48", "#F97316", "#EAB308", "#22C55E", "#3B82F6",
                  "#8B5CF6", "#EC4899", "#D97706", "#881337", "#10B981"
                ];
                const barColor = colors[index % colors.length];
                return (
                  <div key={index} className="flex items-center gap-4">
                    {/* Progress Bar Container */}
                    <div className="flex-1 bg-pink-100 rounded-full h-1 overflow-hidden">
                      <div
                        className="h-full rounded-full shadow-[0_0_8px_rgba(0,0,0,0.05)] transition-all duration-1000"
                        style={{
                          width: `${category.percentage}%`,
                          backgroundColor: barColor
                        }}
                      ></div>
                    </div>
                    {/* Label and Percentage */}
                    <div className="w-26 text-right">
                      <p className="text-[14px] font-bold text-[#1F2937] whitespace-nowrap leading-none mb-0.5" style={{ flexShrink: 1 }}>
                        {category.name}
                      </p>
                      <p className="text-[12px] text-[#92400E] font-medium opacity-80">
                        {category.count} items ({category.percentage}%)
                      </p>
                    </div>
                  </div>
                );
              })}

              {foodAnalytics.categoryDistribution.length === 0 && (
                <div className="py-8 text-center text-[#92400E]/50 font-medium italic text-sm">
                  No category data available to display
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)]">
              <h3 className="text-base font-bold text-[#1F2937] mb-1">Wine Category Distribution</h3>
              <p className="text-[14px] text-gray-400 font-medium mb-6">
                Percentage breakdown of all wines by category. Categories with 0% still display to highlight potential gaps.
              </p>

              <div className="space-y-6">
                {wineAnalytics.categoryDistribution.map((category, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 rounded-[4px]" style={{ backgroundColor: category.color }}></div>
                        <span className="text-[13px] font-bold text-[#4B5563]">{category.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] text-gray-400 font-medium">{category.count} wines</span>
                        <span className="text-[14px] font-bold text-[#1F2937]">{Number(category.percentage || 0).toFixed(1)}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: `${Number(category.percentage || 0).toFixed(1)}%`,
                          backgroundColor: category.color
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Wine Style Coverage Section */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] max-w-[700px]">
              <h3 className="text-base font-bold text-[#1F2937] mb-1">Wine Style Coverage</h3>
              <p className="text-[14px] text-gray-400 font-medium mb-6">
                Displays all wine styles organized by category, showing how many wines fulfill each style. Styles with 0 wines are marked as gaps to help identify opportunities for menu expansion.
              </p>

              <div className="space-y-6">
                <div className="space-y-6">
                  {(() => {
                    const styleDist = wineAnalytics.styleDistribution;

                    const renderStyleSection = (title, items, color) => (
                      <div className="pl-4 border-l-[3px] space-y-3" style={{ borderColor: color }}>
                        <h4 className="text-[14px] font-bold text-[#1F2937] uppercase tracking-wider mb-3">{title}</h4>
                        {items?.map((item, idx) => {
                          const hasWines = item.count > 0;
                          return (
                            <div key={idx} className={`space-y-1.5 ${!hasWines ? 'opacity-50' : ''}`}>
                              <div className="flex justify-between items-end">
                                <span className={`text-[11px] font-bold ${hasWines ? 'text-slate-600' : 'text-slate-400'}`}>
                                  {item.name}
                                </span>
                                <div className="text-right">
                                  <span className={`text-[11px] font-black ${hasWines ? 'text-[#1F2937]' : 'text-gray-400'}`}>
                                    {item.count} wines
                                  </span>
                                  <span className={`text-[11px] font-black ${hasWines ? 'text-[#1F2937]' : 'text-gray-400'}`}>
                                    ({Number(item.percentage || 0).toFixed(1)}%)
                                  </span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-1000"
                                  style={{ width: `${Number(item.percentage || 0).toFixed(1)}%`, backgroundColor: hasWines ? color : '#E5E7EB' }}
                                ></div>
                              </div>
                            </div>
                          );
                        })}
                        {(!items || items.length === 0) && (
                          <p className="text-[11px] text-gray-400 italic">No data available</p>
                        )}
                      </div>
                    );

                    return (
                      <>
                        <>
                          {/* Sparkling */}
                          {styleDist['Sparkling'] && renderStyleSection('Sparkling', styleDist['Sparkling'], '#FACC15')}

                          {/* White Wine */}
                          {styleDist['White'] && renderStyleSection('White Wine', styleDist['White'], '#FEF3C7')}

                          {/* Rosé */}
                          {styleDist['Rosé'] && renderStyleSection('Rosé Wine', styleDist['Rosé'], '#FB7185')}

                          {/* Red Wine */}
                          {styleDist['Red'] && renderStyleSection('Red Wine', styleDist['Red'], '#881337')}

                          {/* Orange Wine */}
                          {styleDist['Orange'] && renderStyleSection('Orange Wine', styleDist['Orange'], '#FB923C')}

                          {/* Dessert Wine */}
                          {styleDist['Dessert'] && renderStyleSection('Dessert Wine', styleDist['Dessert'], '#D97706')}
                        </>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            {/* Regional Distribution Section */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] max-w-[500px]">
              <h3 className="text-base font-bold text-[#1F2937] mb-1">Regional Distribution</h3>
              <p className="text-[14px] text-gray-400 font-medium mb-6">
                Shows the percentage of wines by region. Top 5 regions are displayed; others are grouped into 'Other'.
              </p>

              <div className="space-y-6">
                {Object.entries(wineAnalytics.regions || {})
                  .map(([region, percentage]) => ({ region, percentage }))
                  .sort((a, b) => b.percentage - a.percentage) // Sort by percentage descending
                  .map((item, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <div className="flex justify-between items-end">
                        <span className="text-[14px] text-[#4B5563] font-medium">{item.region}</span>
                        <span className="text-[14px] font-bold text-[#1F2937]">{Number(item.percentage || 0).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-[#881337] rounded-full transition-all duration-1000"
                          style={{ width: `${Number(item.percentage || 0).toFixed(1)}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Most Common Grape Varietals Section */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] max-w-[700px]">
              <h3 className="text-base font-bold text-[#1F2937] mb-1">Most Common Grape Varietals</h3>
              <p className="text-[14px] text-gray-400 font-medium mb-6">
                Top grape varietals across your wine collection, with number of wines and percentage of total.
              </p>

              <div className="grid grid-cols-2 gap-3 mb-3">
                {[
                  { name: "Cabernet Sauvignon", count: wineAnalytics.varietals.cabernetSauvignon || 0, color: "text-[#881337]" },
                  { name: "Chardonnay", count: wineAnalytics.varietals.chardonnay || 0, color: "text-[#D97706]" },
                  { name: "Pinot Noir", count: wineAnalytics.varietals.pinotNoir || 0, color: "text-[#FB7185]" },
                ].map((item, idx) => {
                  const percentage = wineAnalytics.totalWines ? ((item.count / wineAnalytics.totalWines) * 100).toFixed(1) : '0.0';
                  return (
                    <div key={idx} className="bg-[#F9FAFB] rounded-xl p-3 flex flex-col items-center text-center shadow-sm">
                      <h4 className={`text-2xl font-bold ${item.color} leading-none mb-2`}>{item.count}</h4>
                      <p className="text-[14px] font-bold text-[#1F2937] leading-tight mb-1">{item.name}</p>
                      <p className="text-[12px] text-gray-400 font-medium">{percentage}% of wines</p>
                    </div>
                  );
                })}
              </div>

              {(() => {
                const chCount = wineAnalytics.varietals.chardonnay || 0;
                const sumKnown = (wineAnalytics.varietals.cabernetSauvignon || 0) + (wineAnalytics.varietals.chardonnay || 0) + (wineAnalytics.varietals.pinotNoir || 0) + (wineAnalytics.varietals.sauvignonBlanc || 0);
                const otherCount = Math.max(0, (wineAnalytics.totalWines || 0) - sumKnown);
                const chPct = wineAnalytics.totalWines ? ((chCount / wineAnalytics.totalWines) * 100).toFixed(1) : '0.0';
                const otherPct = wineAnalytics.totalWines ? ((otherCount / wineAnalytics.totalWines) * 100).toFixed(1) : '0.0';
                return (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-4 bg-[#F9FAFB] rounded-xl p-3 flex flex-col items-center text-center shadow-sm">
                      <h4 className="text-2xl font-bold text-[#D97706] leading-none mb-2">{chCount}</h4>
                      <p className="text-[14px] font-bold text-[#1F2937] leading-tight mb-1">Chardonnay</p>
                      <p className="text-[12px] text-gray-400 font-medium">{chPct}% of wines</p>
                    </div>
                    <div className="col-span-8 bg-[#F9FAFB] rounded-xl p-3 flex flex-col items-center text-center shadow-sm">
                      <h4 className="text-2xl font-bold text-[#1F2937] leading-none mb-2">{otherCount}</h4>
                      <p className="text-[14px] font-bold text-[#1F2937] leading-tight mb-1">Other Varietals</p>
                      <p className="text-[12px] text-gray-400 font-medium">{otherPct}% of wines</p>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Wine Characteristics & Composition Section */}
            <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] max-w-[700px]">
              <h3 className="text-base font-bold text-[#1F2937] mb-1">Wine Characteristics & Composition</h3>
              <p className="text-[14px] text-gray-400 font-medium mb-6">
                Breakdown of sustainability, filtration, and sweetness profile attributes across your wine list.
              </p>

              <div className="bg-[#FFF7ED] rounded-xl p-3">
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { name: "Organic", value: wineAnalytics.characteristics?.organic, icon: "🌿", type: "count" },
                    { name: "Biodynamic", value: wineAnalytics.characteristics?.biodynamic, icon: "🌍", type: "count" },
                    { name: "Vegan", value: wineAnalytics.characteristics?.vegan, icon: "🥬", type: "count" },
                    { name: "Unfiltered", value: wineAnalytics.characteristics?.unfiltered, icon: "🌫️", type: "percent" },
                    { name: "Dry Wines", value: wineAnalytics.characteristics?.dryWines, icon: "💧", type: "percent" },
                    { name: "Residual Sugar", value: wineAnalytics.characteristics?.residualSugar, icon: "🍯", type: "percent" },
                  ].map((item, idx) => {
                    const rawValue = item.value || 0;
                    let numVal = 0;
                    let isPctString = false;

                    // Handle percentage strings (e.g., "58.8%")
                    if (typeof rawValue === 'string' && rawValue.includes('%')) {
                      numVal = parseFloat(rawValue);
                      isPctString = true;
                    } else {
                      numVal = Number(rawValue);
                    }

                    let displayStr = "";
                    if (item.type === "count") {
                      // If we received a percentage string but expect a count, back-calculate count
                      if (isPctString) {
                        const calculatedCount = Math.round((numVal / 100) * (wineAnalytics.totalWines || 0));
                        displayStr = `${calculatedCount} (${numVal.toFixed(1)}%)`;
                      } else {
                        // We received a raw count
                        const pct = wineAnalytics.totalWines ? ((numVal / wineAnalytics.totalWines) * 100).toFixed(1) : '0.0';
                        displayStr = `${numVal} (${pct}%)`;
                      }
                    } else {
                      // Prioritize existing percentage format
                      displayStr = `${numVal.toFixed(1)}%`;
                    }

                    return (
                      <div key={idx} className="bg-white rounded-xl py-3 px-2 flex flex-col items-center text-center shadow-sm">
                        <span className="text-xl mb-1.5">{item.icon}</span>
                        <p className="text-[14px] font-bold text-[#1F2937] leading-tight mb-0.5">{item.name}</p>
                        <p className="text-[12px] text-gray-500 font-medium">{displayStr}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Recent Wine Updates Section - Hidden for employees */}
            {userRole !== "employee" && (
              <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] max-w-[500px]">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-base font-bold text-[#1F2937] mb-1">Recent Wine Updates</h3>
                    <p className="text-[14px] text-gray-400 font-medium">Shows recent wine item activity for this restaurant.</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowAllUpdatesModal(true);
                      setCurrentPage(1);
                    }}
                    className="text-[14px] font-bold text-[#881337] uppercase tracking-tight hover:underline"
                  >
                    View All
                  </button>
                </div>

                <div className="space-y-3">
                  {wineAnalytics.recentWineUpdates.length === 0 ? (
                    <div className="py-8 text-center text-gray-400">No recent wine activity</div>
                  ) : (
                    wineAnalytics.recentWineUpdates.slice(0, 10).map((u, index) => (
                      <div key={index} className={`border rounded-xl p-3 flex flex-col group transition-all hover:shadow-sm`}>
                        <div className="flex justify-between items-start mb-0.5">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 ${u.action?.includes('archived') ? 'bg-red-500' : u.action?.includes('created') ? 'bg-blue-500' : 'bg-emerald-500'} rounded-full`}></div>
                            <h4 className="text-[14px] font-bold text-[#1F2937] leading-tight">{mapActionToTitle(u.action)}</h4>
                          </div>
                          <p className="text-[12px] text-gray-400 font-medium">{timeSince(u.timestamp)}</p>
                        </div>
                        <div className="pl-3.5">
                          <p className="text-[14px] font-bold text-[#4B5563] leading-tight mb-0.5">{u.details?.description || u.details?.wine_uuid || '—'}</p>
                          <p className="text-[12px] text-gray-400 font-medium">by {u.user_uuid?.first_name || ''} {u.user_uuid?.last_name || ''}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dietary Accommodations Section */}
        {activeTab === "food" && (
          <>
            <div className="mt-8 bg-white">
              <div className="mb-5">
                <h3 className="text-base font-bold text-[#1F2937] leading-tight mb-1">Dietary Accommodations</h3>
                <p className="text-[14px] text-gray-400 font-medium">
                  Percentage of dishes supporting dietary needs.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: "Vegan", count: dishAnalyticsData?.veganDishes || 0, icon: "🌱", color: "#F0FDF4" },
                  { name: "Vegetarian", count: dishAnalyticsData?.vegetarianDishes || 0, icon: "🥗", color: "#F0FDF4" },
                  { name: "Gluten-Free", count: dishAnalyticsData?.gluttenFreeDishes || 0, icon: "🌾", color: "#FEF3C7" },
                  { name: "Dairy-Free", count: dishAnalyticsData?.dairyFreeDishes || 0, icon: "🥛", color: "#F9FAFB" },
                  { name: "Nut-Free", count: dishAnalyticsData?.nuttFreeDishes || 0, icon: "🌰", color: "#FFFAF2" },
                  { name: "Soy-Free", count: dishAnalyticsData?.soyFreeDishes || 0, icon: "🫘", color: "#FFFAF2" },
                  { name: "Sesame-Free", count: dishAnalyticsData?.sesameFreeDishes || 0, icon: "🌱", color: "#F0FDF4" },
                ].map((diet, index) => {
                  const percentage = dishAnalyticsData?.totalDishes ? ((diet.count / dishAnalyticsData.totalDishes) * 100).toFixed(1) : "0.0";
                  return (
                    <div key={index} className="bg-[#FFFAF2] rounded-xl p-3 border border-orange-50/50 flex flex-col items-center text-center shadow-sm">
                      <span className="text-xl mb-2">{diet.icon}</span>
                      <p className="text-[14px] font-medium text-gray-500 uppercase tracking-tight mb-1">{diet.name}</p>
                      <h4 className="text-xl font-bold text-[#1F2937] leading-none mb-0.5">{diet.count}</h4>
                      <p className="text-[12px] text-gray-400 font-medium">{percentage}%</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-10 bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)]">
              <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-4">
                <div className="max-w-xl">
                  <h3 className="text-base font-bold text-[#1F2937] uppercase tracking-wider mb-2">Allergen Breakdown</h3>
                  <p className="text-[14px] text-gray-400 font-medium leading-relaxed">
                    Key allergens prevalence across items.
                  </p>
                </div>

                {dishAnalyticsData?.totalDishes > 0 && (
                  <div className="bg-[#FEF3C7]/50 border border-[#FDE68A] rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="text-[#881337]">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V14M12 17.5V18M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[13px] text-gray-500 font-bold uppercase tracking-tight">Most Common Allergen</p>
                      <p className="text-[14px] font-bold text-[#881337]">
                        {(() => {
                          const allergens = [
                            { name: "Milk", count: dishAnalyticsData?.milkDishes || 0 },
                            { name: "Eggs", count: dishAnalyticsData?.eggsDishes || 0 },
                            { name: "Fish", count: dishAnalyticsData?.fishDishes || 0 },
                            { name: "Shellfish", count: dishAnalyticsData?.shellfishDishes || 0 },
                            { name: "Tree Nuts", count: dishAnalyticsData?.treeNutsDishes || 0 },
                            { name: "Peanuts", count: dishAnalyticsData?.peanutsDishes || 0 },
                            { name: "Wheat", count: dishAnalyticsData?.wheatDishes || 0 },
                            { name: "Soy", count: dishAnalyticsData?.soyDishes || 0 },
                            { name: "Sesame", count: dishAnalyticsData?.sesameDishes || 0 },
                          ];
                          if (!dishAnalyticsData?.totalDishes) return "None";
                          const mostCommon = allergens.reduce((prev, current) => (prev.count > current.count) ? prev : current);
                          const percentage = ((mostCommon.count / dishAnalyticsData.totalDishes) * 100).toFixed(1);
                          return `${mostCommon.name} (${percentage}%)`;
                        })()}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: "Milk", count: dishAnalyticsData?.milkDishes || 42, icon: "🥛" },
                  { name: "Eggs", count: dishAnalyticsData?.eggsDishes || 28, icon: "🍳" },
                  { name: "Fish", count: dishAnalyticsData?.fishDishes || 15, icon: "🐟" },
                  { name: "Shellfish", count: dishAnalyticsData?.shellfishDishes || 8, icon: "🦐" },
                  { name: "Tree Nuts", count: dishAnalyticsData?.treeNutsDishes || 12, icon: "🌰" },
                  { name: "Peanuts", count: dishAnalyticsData?.peanutsDishes || 5, icon: "🥜" },
                  { name: "Wheat", count: dishAnalyticsData?.wheatDishes || 58, icon: "🌾" },
                  { name: "Soy", count: dishAnalyticsData?.soyDishes || 18, icon: "🫘" },
                  { name: "Sesame", count: dishAnalyticsData?.sesameDishes || 8, icon: "🌱" },
                ].map((allergen, index) => {
                  const percentage = dishAnalyticsData?.totalDishes ? ((allergen.count / dishAnalyticsData.totalDishes) * 100).toFixed(1) : "0.0";
                  return (
                    <div key={index} className="bg-[#FFFAF2] rounded-xl p-2.5 flex items-center gap-2.5 border border-orange-50/30">
                      <span className="text-lg">{allergen.icon}</span>
                      <div className="flex flex-col">
                        <p className="text-[14px] font-bold text-[#1F2937]">{allergen.name}</p>
                        <p className="text-[12px] text-gray-400 font-medium">
                          {allergen.count} dishes • {percentage}%
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Recent Menu Updates Section - Restrict to Food Tab or handle Wine Updates if needed */}
        {activeTab === "food" && (
          <div className="mt-12 bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)]">
            <div className="flex justify-between items-center mb-5">
              <div>
                <h3 className="text-base font-bold text-[#1F2937] uppercase tracking-wider mb-1">Recent Menu Updates</h3>
                <p className="text-[14px] text-gray-400 font-medium">Recent changes to your menu.</p>
              </div>
              <button
                onClick={() => {
                  setShowAllUpdatesModal(true);
                  setCurrentPage(1);
                }}
                className="text-[14px] font-bold text-[#881337] uppercase tracking-tight hover:underline"
              >
                View All
              </button>
            </div>

            <div className="space-y-3">
              {(!dishAnalyticsData?.recentDishUpdates || dishAnalyticsData.recentDishUpdates.length === 0) ? (
                <div className="py-8 text-center text-gray-400">No recent menu activity</div>
              ) : (
                dishAnalyticsData.recentDishUpdates.slice(0, 10).map((u, index) => (

                  <div key={index} className={`border rounded-xl p-3 flex flex-col group transition-all hover:shadow-sm`}>
                    <div className="flex justify-between items-start mb-0.5">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 ${u.action?.includes('archived') ? 'bg-red-500' : u.action?.includes('created') ? 'bg-blue-500' : 'bg-emerald-500'} rounded-full`}></div>
                        <h4 className="text-[14px] font-bold text-[#1F2937] leading-tight">{mapActionToTitle(u.action)}</h4>
                      </div>
                      <p className="text-[12px] text-gray-400 font-medium">{timeSince(u.timestamp)}</p>
                    </div>
                    <div className="pl-3.5">
                      <p className="text-[14px] font-bold text-[#4B5563] leading-tight mb-0.5">{u.details?.description || u.details?.dish_uuid || '—'}</p>
                      <p className="text-[12px] text-gray-400 font-medium">by {u.user_uuid?.first_name || ''} {u.user_uuid?.last_name || ''}</p>
                    </div>
                  </div>
                ))
              )}

            </div>
          </div>
        )}
      </div>

      {/* All Updates Modal */}
      {showAllUpdatesModal && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowAllUpdatesModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-[#881337] to-[#BE123C]">
              <div>
                <h2 className="text-xl font-bold text-white">
                  All Recent {activeTab === "food" ? "Menu" : "Wine"} Updates
                </h2>
                <p className="text-sm text-white/80 mt-1">
                  {restaurant?.name}
                </p>
              </div>
              <button
                onClick={() => setShowAllUpdatesModal(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <FaTimes className="text-white" size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {(() => {
                const updates = activeTab === "food"
                  ? (dishAnalyticsData?.recentDishUpdates || [])
                  : (wineAnalyticsData?.recentWineUpdates || []);

                const totalPages = Math.ceil(updates.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const currentUpdates = updates.slice(startIndex, endIndex);

                if (updates.length === 0) {
                  return (
                    <div className="py-12 text-center text-gray-400">
                      <p className="text-lg font-medium">No recent {activeTab === "food" ? "menu" : "wine"} activity</p>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="space-y-2">
                      {currentUpdates.map((u, index) => (
                        <div
                          key={startIndex + index}
                          className="border border-gray-200 rounded-lg p-2.5 hover:shadow-md transition-all bg-[#FFFAF2]"
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-1.5">
                              <div className={`w-2 h-2 ${u.action?.includes('archived') ? 'bg-red-500' :
                                u.action?.includes('created') ? 'bg-blue-500' :
                                  'bg-emerald-500'
                                } rounded-full`}></div>
                              <h4 className="text-sm font-bold text-[#1F2937]">
                                {mapActionToTitle(u.action)}
                              </h4>
                            </div>
                            <p className="text-xs text-gray-400 font-medium">
                              {timeSince(u.timestamp)}
                            </p>
                          </div>
                          <div className="pl-3.5">
                            <p className="text-sm font-bold text-[#4B5563] mb-0.5">
                              {u.details?.description || u.details?.dish_uuid || u.details?.wine_uuid || '—'}
                            </p>
                            <p className="text-xs text-gray-400 font-medium">
                              by {u.user_uuid?.first_name || ''} {u.user_uuid?.last_name || ''}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                        <div className="text-sm text-gray-500">
                          Showing {startIndex + 1} to {Math.min(endIndex, updates.length)} of {updates.length} updates
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${currentPage === 1
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-[#881337] text-white hover:bg-[#BE123C]'
                              }`}
                          >
                            Previous
                          </button>

                          <div className="flex items-center gap-1">
                            {[...Array(totalPages)].map((_, i) => {
                              const pageNum = i + 1;
                              // Show first page, last page, current page, and pages around current
                              if (
                                pageNum === 1 ||
                                pageNum === totalPages ||
                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                              ) {
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${currentPage === pageNum
                                      ? 'bg-[#881337] text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                      }`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              } else if (
                                pageNum === currentPage - 2 ||
                                pageNum === currentPage + 2
                              ) {
                                return <span key={pageNum} className="text-gray-400">...</span>;
                              }
                              return null;
                            })}
                          </div>

                          <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${currentPage === totalPages
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-[#881337] text-white hover:bg-[#BE123C]'
                              }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
